import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { AUTH_BEARER_TOKEN_KEY } from 'src/constants';
import { getLocalStorageItem } from 'src/hooks/useLocalStorage';
import { clientAuthRealm, centralAuthRealm, getApiUrl } from 'src/utils/env';
import { refreshAccessToken } from 'src/utils/tokenRefresher';

const axiosInstance = axios.create({
  baseURL: getApiUrl(),
  // default is bracket format: ?id[]=1&id[]=2
  // indexes null is replicated param format: ?id=1&id=2
  paramsSerializer: {
    indexes: null
  }
});

axiosInstance.interceptors.request.use(
  config => {
    const token = getLocalStorageItem<string>(AUTH_BEARER_TOKEN_KEY);

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    config.headers['X-Client-Name'] = 'submission-client';
    // config.headers['X-Client-Version'] = '1.0.0';

    config.headers['X-Auth-Realm'] = clientAuthRealm;
    config.headers['X-Auth-Central-Auth-Realm'] = centralAuthRealm;

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

axiosInstance.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;

    // On a 401, silently renew the (possibly expired) token and retry the
    // request once. The provider persists the fresh token to localStorage and
    // we also set it on the retried config, so the retry cannot go out with a
    // stale header. If refresh fails (e.g. refresh token expired), the
    // provider's refresh handler logs the user out.
    if (error.response?.status === 401 && config && !config._retry) {
      config._retry = true;
      if (await refreshAccessToken()) {
        const token = getLocalStorageItem<string>(AUTH_BEARER_TOKEN_KEY);
        if (token) config.headers.set('Authorization', `Bearer ${token}`);
        return axiosInstance(config);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
